import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import moment from 'moment'
import { fetchData, toasterrormsg } from '../../../reusable'
import { KTIcon } from '../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import Loader from '../components/Loader'
import { Tr } from '../../../_metronic/i18n/Tr'
import { DynamicChart, TooltipField } from '../dashboard/DashboardChartDynamic'
import { selectStyles } from '../analytics/components/selectStyles'

const TOOLTIP_FIELDS: TooltipField[] = [
  { key: 'revenue', label: 'Revenue', format: 'number' },
  { key: 'totalAds', label: 'Total Ads', format: 'raw' },
]

const START_YEAR = 2020

type Option = { value: string; label: string }

// Keep the dropdown menus above the chart card (ApexCharts sets its own stacking context).
const filterSelectStyles = {
  ...selectStyles,
  menu: (base: any) => ({ ...base, zIndex: 1056 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 1056 }),
}

const buildQueryString = (params: Record<string, any>) => {
  const query = Object.entries(params)
    .filter(([, value]) => value !== '' && value !== null && value !== undefined)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&')
  return query ? `?${query}` : ''
}

const RevenueByPlacements: FC = () => {
  const t = Tr()

  const [isLoading, setIsLoading] = useState(false)
  const [chartData, setChartData] = useState<{ name: string; data: Record<string, any>[] }[]>([])
  const [chartCategories, setChartCategories] = useState<string[]>([])
  const [totalRevenue, setTotalRevenue] = useState('0')
  const [chartLength, setChartLength] = useState('0')

  // ── Filters ──
  const [selectedYear, setSelectedYear] = useState<Option | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<Option | null>(null)
  const [selectedPlacement, setSelectedPlacement] = useState<Option | null>(null)
  const [placementOptions, setPlacementOptions] = useState<Option[]>([])
  const [placementsLoading, setPlacementsLoading] = useState(true)

  const yearOptions = useMemo<Option[]>(() => {
    const currentYear = moment().year()
    return Array.from({ length: currentYear - START_YEAR + 1 }, (_, i) => ({
      value: String(currentYear - i),
      label: String(currentYear - i),
    }))
  }, [])

  const monthOptions = useMemo<Option[]>(
    () =>
      moment.months().map((name, index) => ({
        value: String(index + 1),
        label: t(name),
      })),
    [t]
  )

  const hasActiveFilter = Boolean(selectedYear || selectedMonth || selectedPlacement)

  const emptyMessage = hasActiveFilter
    ? t('No revenue found for the selected filters — try a different year, month or placement')
    : t('No placement revenue data available')

  // ── Placement (ads setting) list — sub API, never blocks the page ──
  const fetchPlacementOptions = useCallback(async () => {
    try {
      setPlacementsLoading(true)
      const response = await fetchData('admin/ads/setting/list', {})
      if (response?.status == '200') {
        const list = response?.data?.list ?? response?.data ?? []
        const options = (Array.isArray(list) ? list : [])
          .filter((item: any) => item?.adssettingId != null)
          .map((item: any) => ({
            value: String(item.adssettingId),
            label: item.placementName || item.name || `#${item.adssettingId}`,
          }))
        setPlacementOptions(options)
      }
    } catch (error) {
      console.error('Error fetching ads setting list:', error)
    } finally {
      setPlacementsLoading(false)
    }
  }, [])

  const fetchPlacementRevenue = useCallback(async () => {
    try {
      setIsLoading(true)
      const param = {
        year: selectedYear?.value ?? '',
        month: selectedMonth?.value ?? '',
        adssettingId: selectedPlacement?.value ?? '',
      }
      // Sent both as query string and body so the filters apply regardless of
      // whether the endpoint reads req.query or req.body.
      const response = await fetchData(
        `admin/ads/placementwise/revenue${buildQueryString(param)}`,
        param
      )
      if (response.status == '200') {
        const list = response.data?.list || response.data || []
        buildChart(Array.isArray(list) ? list : [])
      } else {
        toasterrormsg(response?.message || 'An error occurred')
        resetChart()
      }
    } catch (error) {
      console.error('Error fetching placement revenue:', error)
      toasterrormsg('Failed to fetch placement revenue')
      resetChart()
    } finally {
      setIsLoading(false)
    }
  }, [selectedYear, selectedMonth, selectedPlacement])

  const buildChart = (list: any[]) => {
    if (!list || list.length === 0) {
      resetChart()
      return
    }

    const categories = list.map((item: any) => item.placementName || item.name || 'Unknown')
    const dataPoints = list.map((item: any) => ({
      x: item.placementName || item.name || 'Unknown',
      y: parseFloat(item.revenue || item.totalRevenue || item.amount || 0),
      revenue: parseFloat(item.revenue || item.totalRevenue || item.amount || 0),
      totalAds:  item.totalAds || 0,
    }))

    const total = dataPoints.reduce((acc: number, p: any) => acc + p.revenue, 0)

    // The API returns every placement even when none earned anything — an
    // all-zero chart is noise, so treat it as no data.
    if (!total) {
      resetChart()
      return
    }

    setChartCategories(categories)
    setChartData([{ name: '', data: dataPoints }])
    setTotalRevenue(String(total))
    setChartLength(String(categories.length))
  }

  const resetChart = () => {
    setChartData([])
    setChartCategories([])
    setTotalRevenue('0')
    setChartLength('0')
  }

  const handleYearChange = (option: Option | null) => {
    setSelectedYear(option)
    // Month is meaningless without a year — drop it when the year is cleared.
    if (!option) setSelectedMonth(null)
  }

  const clearFilters = () => {
    setSelectedYear(null)
    setSelectedMonth(null)
    setSelectedPlacement(null)
  }

  useEffect(() => {
    fetchPlacementOptions()
  }, [fetchPlacementOptions])

  useEffect(() => {
    fetchPlacementRevenue()
  }, [fetchPlacementRevenue])

  useEffect(() => {
    document.title = 'Insayr | Revenue By Placements'
  }, [])

  return (
    <>
      <ToastContainer />
      {isLoading && <Loader />}

      {/* Info Banner */}
      <div className='vs-info-banner mb-5'>
        <KTIcon iconName='chart-simple' className='fs-1 text-primary flex-shrink-0' />
        <div>
          <div className='fw-bold fs-6 text-primary mb-1'>{t('Revenue By Placements')}</div>
          <p className='text-gray-700 fs-7 mb-0'>
            {t('Revenue breakdown by ad placement. Hover over each bar to see the revenue and total ads for that placement.')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className='card border-0 shadow-sm mb-5'>
        <div className='card-body py-4 px-6'>
          <div className='d-flex flex-wrap align-items-center gap-3'>
            <div className='d-flex align-items-center gap-2 text-gray-600 fs-7 fw-semibold'>
              <KTIcon iconName='filter' className='fs-4' />
              <span>{t('Filters')}</span>
            </div>

            <div style={{ minWidth: 150 }}>
              <Select
                options={yearOptions}
                value={selectedYear}
                onChange={(val: any) => handleYearChange(val)}
                isClearable
                placeholder={t('All Years')}
                styles={filterSelectStyles}
                menuPortalTarget={document.body}
                menuPosition='fixed'
              />
            </div>

            <div style={{ minWidth: 170 }}>
              <Select
                options={monthOptions}
                value={selectedMonth}
                onChange={(val: any) => setSelectedMonth(val)}
                isClearable
                isDisabled={!selectedYear}
                placeholder={selectedYear ? t('All Months') : t('Select a year first')}
                styles={filterSelectStyles}
                menuPortalTarget={document.body}
                menuPosition='fixed'
              />
            </div>

            <div style={{ minWidth: 220 }}>
              <Select
                options={placementOptions}
                value={selectedPlacement}
                onChange={(val: any) => setSelectedPlacement(val)}
                isClearable
                isLoading={placementsLoading}
                placeholder={t('All Placements')}
                styles={filterSelectStyles}
                menuPortalTarget={document.body}
                menuPosition='fixed'
              />
            </div>

            {hasActiveFilter && (
              <button
                type='button'
                className='btn btn-light-danger btn-sm d-flex align-items-center gap-1'
                onClick={clearFilters}
              >
                <KTIcon iconName='cross-circle' className='fs-5' />
                {t('Clear')}
              </button>
            )}
          </div>
        </div>
      </div>

      <DynamicChart
        className='card-xl-stretch mb-5 mb-xl-8'
        title='Total Revenue'
        totalValue={totalRevenue}
        categories={chartCategories}
        data={chartData}
        tooltipFields={TOOLTIP_FIELDS}
        length={chartLength}
        emptyMessage={emptyMessage}
      />
    </>
  )
}

export { RevenueByPlacements }
